import { Record } from 'immutable';

// Statuses
const STATUS_IN_PROGRESS = 0; // In Progress (Application)
const STATUS_PENDING_DECISION = 1; // Pending Decision (Selection)
const STATUS_APPROVED_NO_EMAIL = 2; // Approved - No email (Approved)
const STATUS_APPROVED_ONBOARDING_SESSION = 3; // Approved - Onboarding Session (Approved)
const STATUS_UNMATCHED = 4; // Un-matched (Matching)
const STATUS_MATCHED = 5; // Matched (Matching)
const STATUS_INCUBATING = 6; // Incubating (Incubation)
const STATUS_ALUMNI = 7; // Alumni (Alumni)

export const getStatusDisplay = (t, status) => {
  const statusNames = {
    [STATUS_IN_PROGRESS]: t('In Progress'),
    [STATUS_PENDING_DECISION]: t('Pending Decision'),
    [STATUS_APPROVED_NO_EMAIL]: t('Manual Follow-Up'),
    [STATUS_APPROVED_ONBOARDING_SESSION]: t('Onboarding'),
    [STATUS_UNMATCHED]: t('Unmatched'),
    [STATUS_MATCHED]: t('Matched'),
    [STATUS_INCUBATING]: t('Incubating'),
    [STATUS_ALUMNI]: t('Completed'),
  };
  return statusNames[status];
};

// Dropped reasons
const DROP_UNRESPONSIVE = 0;
const DROP_NOT_A_FIT = 1;
const DROP_BAD_EXPERIENCE_PM = 2;
const DROP_BAD_EXPERIENCE_E = 3;
const DROP_BAD_TIMING = 4;
const DROP_NOT_INTERESTED_PROJECTS = 5;
const DROP_REJECTED_BY_E = 6;
const DROP_FOR_PROJECT_DROPPED = 7;

const DROPPED_REASONS = [
  DROP_UNRESPONSIVE,
  DROP_NOT_A_FIT,
  DROP_BAD_EXPERIENCE_PM,
  DROP_BAD_EXPERIENCE_E,
  DROP_BAD_TIMING,
  DROP_NOT_INTERESTED_PROJECTS,
  DROP_REJECTED_BY_E,
  DROP_FOR_PROJECT_DROPPED,
];

export const getDroppedReasonDisplay = (t, droppedReason) => {
  switch (droppedReason) {
    case DROP_UNRESPONSIVE:
      return t('Became unresponsive');
    case DROP_NOT_A_FIT:
      return t('Not a fit');
    case DROP_BAD_EXPERIENCE_PM:
      return t('Had a bad experience with Program Manager');
    case DROP_BAD_EXPERIENCE_E:
      return t('Had a bad experience with Entrepreneur');
    case DROP_BAD_TIMING:
      return t('Bad timing');
    case DROP_NOT_INTERESTED_PROJECTS:
      return t('No interest in projects');
    case DROP_REJECTED_BY_E:
      return t('Was rejected by Entrepreneur(s)');
    case DROP_FOR_PROJECT_DROPPED:
      return t('The project was dropped');
    default:
      return undefined;
  }
};

export const getMentorDroppedReasonOptions = t =>
  DROPPED_REASONS.map(reason => ({
    value: reason,
    label: getDroppedReasonDisplay(t, reason),
  }));

// Rejected reasons
const REJ_INCOMPLETE = 0;
const REJ_PROF_EXPERIENCE = 1;
const REJ_ENT_KNOWLEDGE = 2;
const REJ_LAN_PROFICIENCY = 3;
const REJ_PROGRAM_REQUIREMENTS = 4;
const REJ_OTHER = 5;
const REJ_DUE_PROGRAM_COPY = 6;

const REJECTED_REASONS = [
  REJ_INCOMPLETE,
  REJ_PROF_EXPERIENCE,
  REJ_ENT_KNOWLEDGE,
  REJ_LAN_PROFICIENCY,
  REJ_PROGRAM_REQUIREMENTS,
  REJ_OTHER,
];

export const getRejectedReasonDisplay = (t, rejectedReason) => {
  switch (rejectedReason) {
    case REJ_INCOMPLETE:
      return t('Incomplete / weak application');
    case REJ_PROF_EXPERIENCE:
      return t('Lack of professional experience');
    case REJ_ENT_KNOWLEDGE:
      return t('Lack of entrepreneurship knowledge');
    case REJ_LAN_PROFICIENCY:
      return t('Lack of language proficiency');
    case REJ_PROGRAM_REQUIREMENTS:
      return t('Does not meet program requirements');
    case REJ_OTHER:
      return t('Other (no email sent)');
    case REJ_DUE_PROGRAM_COPY:
      return t('Due to program copy (no email sent)');
    default:
      return undefined;
  }
};

export const getMentorRejectedReasonOptions = t =>
  REJECTED_REASONS.map(reason => ({
    value: reason,
    label: getRejectedReasonDisplay(t, reason),
    text: getRejectedReasonDisplay(t, reason),
  }));

// Accept/Move to a stage
export const getAcceptMoveStageDisplay = (t, status) => {
  switch (status) {
    case STATUS_IN_PROGRESS:
      return t('Move to Application');
    case STATUS_PENDING_DECISION:
      return t('Move to Selection');
    case STATUS_APPROVED_NO_EMAIL:
      return t('Accept, but no email sent');
    case STATUS_APPROVED_ONBOARDING_SESSION:
      return t('Accept & Attend Onboarding Session');
    case STATUS_UNMATCHED:
      return t('Accept & move directly to MATCHING');
    default:
      return undefined;
  }
};

const ACCEPT_MOVE_STAGES = [
  STATUS_IN_PROGRESS,
  STATUS_PENDING_DECISION,
  STATUS_APPROVED_NO_EMAIL,
  STATUS_APPROVED_ONBOARDING_SESSION,
  STATUS_UNMATCHED,
];

export const getMentorAcceptMoveStageOptions = t =>
  ACCEPT_MOVE_STAGES.map(stage => ({
    value: stage,
    label: getStatusDisplay(t, stage),
  }));

export const getMPMentorStatus = app => {
  const mentorStatus = {
    [STATUS_IN_PROGRESS]: 'application',
    [STATUS_PENDING_DECISION]: 'selection',
    [STATUS_APPROVED_NO_EMAIL]: 'approved',
    [STATUS_APPROVED_ONBOARDING_SESSION]: 'approved',
    [STATUS_UNMATCHED]: 'unmatched',
    [STATUS_MATCHED]: 'matched',
    [STATUS_INCUBATING]: 'incubating',
    [STATUS_ALUMNI]: 'alumni',
  };
  if (app.rejected !== null) return 'rejected';
  if (app.dropped !== null) return 'dropped';
  return mentorStatus[app.status] || 'application';
};

const MentorApplicationRecord = Record({
  id: null,
  mentor: null,
  program: null,
  additional_answer_1: null,
  additional_answer_2: null,
  additional_answer_3: null,
  additional_answer: null,
  complete_surveys: null,
  notes: null,
  matching_notes: '',
  application_notes: '',
  dropped_notes: '',
  incubating_notes: '',
  last_follow_up_date: null,
  participate_in_another_program_if_rejected: null,
  program_type: null,
  progress: null,
  ranking_max_projects: null,
  invites_pending_sign: 0,
  ranking_open_to_comentor: null,
  ranking_reasons: null,
  ranking_submitted: null,
  ranking: null,
  rejected: null,
  remark: null,
  review_status: null,
  STATUS_APPROVED_NO_EMAIL,
  STATUS_ALUMNI,
  STATUS_IN_PROGRESS,
  STATUS_INCUBATING,
  STATUS_MATCHED,
  STATUS_PENDING_DECISION,
  STATUS_APPROVED_ONBOARDING_SESSION,
  STATUS_UNMATCHED,
  status: null,
  survey_responses_eoi_ment: null,
  survey_responses_pc_ment: null,
});

class MentorApplication extends MentorApplicationRecord {
  // Statuses
  static STATUS_IN_PROGRESS = STATUS_IN_PROGRESS;
  static STATUS_PENDING_DECISION = STATUS_PENDING_DECISION;
  static STATUS_APPROVED_NO_EMAIL = STATUS_APPROVED_NO_EMAIL;
  static STATUS_APPROVED_ONBOARDING_SESSION = STATUS_APPROVED_ONBOARDING_SESSION;
  static STATUS_UNMATCHED = STATUS_UNMATCHED;
  static STATUS_MATCHED = STATUS_MATCHED;
  static STATUS_INCUBATING = STATUS_INCUBATING;
  static STATUS_ALUMNI = STATUS_ALUMNI;

  // Rejected reasons
  static REJ_INCOMPLETE = REJ_INCOMPLETE;
  static REJ_PROF_EXPERIENCE = REJ_PROF_EXPERIENCE;
  static REJ_ENT_KNOWLEDGE = REJ_ENT_KNOWLEDGE;
  static REJ_LAN_PROFICIENCY = REJ_LAN_PROFICIENCY;
  static REJ_PROGRAM_REQUIREMENTS = REJ_PROGRAM_REQUIREMENTS;
  static REJ_OTHER = REJ_OTHER;

  // Dropped reasons
  static DROP_UNRESPONSIVE = DROP_UNRESPONSIVE;
  static DROP_NOT_A_FIT = DROP_NOT_A_FIT;
  static DROP_BAD_EXPERIENCE_PM = DROP_BAD_EXPERIENCE_PM;
  static DROP_BAD_EXPERIENCE_E = DROP_BAD_EXPERIENCE_E;
  static DROP_BAD_TIMING = DROP_BAD_TIMING;
  static DROP_NOT_INTERESTED_PROJECTS = DROP_NOT_INTERESTED_PROJECTS;
  static DROP_REJECTED_BY_E = DROP_REJECTED_BY_E;
  static DROP_FOR_PROJECT_DROPPED = DROP_FOR_PROJECT_DROPPED;

  get id() {
    return this.get('id');
  }

  getRejectedReasonDisplay = t => getRejectedReasonDisplay(t, this.get('rejected')); // Not used anywhere right now
}

export default MentorApplication;
